import React from 'react'
import { Paper, Typography, Box } from '@mui/material'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white px-4 py-3 rounded-xl shadow-lg border border-emerald-100">
                <p className="font-semibold text-gray-800">Année {label}</p>
                <p className="text-sm text-gray-600">
                    Plantés cette année :{' '}
                    <span className="font-semibold text-gray-800">
                        {payload[0].payload.total_tree_planted?.toLocaleString('fr-FR')}
                    </span>
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                    Cumul total : <span className="font-bold">{payload[0].value?.toLocaleString('fr-FR')}</span>
                </p>
            </div>
        )
    }
    return null
}

const PlantationYearlyCumulativeChart = ({ data = [] }) => {
    const latestTotal = data.length ? data[data.length - 1].cumulative_total : 0
    const currentYearPlanted = data.length ? data[data.length - 1].total_tree_planted : 0

    return (
        <Paper
            elevation={0}
            style={{ padding: 24, borderRadius: 16, backgroundColor: '#FFFFFF', border: '1px solid #f0f0f0' }}
        >
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="font-semibold text-gray-800">Évolution cumulative du nombre de plants plantés</p>
                    <Typography variant="caption" className="text-gray-400">
                        Progression totale depuis le début du suivi
                    </Typography>
                </div>
                <div className="flex items-center gap-2 bg-emerald-50 px-3 py-2 rounded-lg">
                    <TrendingUpIcon style={{ fontSize: 18, color: '#059669' }} />
                    <div>
                        <p className="text-[10px] text-emerald-600 leading-none">Total cumulé</p>
                        <p className="text-base font-bold text-emerald-700 leading-tight">
                            {latestTotal.toLocaleString('fr-FR')}
                        </p>
                    </div>
                </div>
            </div>

            <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                    <defs>
                        <linearGradient id="colorCumul" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                        dataKey="year"
                        stroke="#9ca3af"
                        tick={{ fill: '#6b7280', fontSize: 13 }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                        padding={{ left: 20, right: 20 }}
                    />
                    <YAxis
                        stroke="#9ca3af"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        width={45}
                    />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '4 4' }}
                    />
                    <Area
                        type="monotone"
                        dataKey="cumulative_total"
                        stroke="#059669"
                        strokeWidth={3}
                        fill="url(#colorCumul)"
                        dot={{ fill: '#059669', r: 5, strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 7, fill: '#047857', stroke: '#fff', strokeWidth: 2 }}
                        name="Cumul plants plantés"
                    />
                </AreaChart>
            </ResponsiveContainer>

            {data.length === 1 && (
                <Box className="text-center mt-2">
                    <Typography variant="caption" className="text-gray-400 italic">
                        La courbe se dessinera au fur et à mesure des années de plantation suivantes.
                    </Typography>
                </Box>
            )}
        </Paper>
    )
}

export default PlantationYearlyCumulativeChart
